import React from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { fmtDateET, fmtMoney, statusBadge, statusLabel } from './paymentUtils';
import { QboRefs } from './QboRefs';
import { PaymentRowActions } from './PaymentRowActions';

const StatusPill = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(status)}`}>
    {statusLabel(status)}
  </span>
);

const SortHeader = ({ label, field, sort, dir, onSort, className = '' }) => (
  <th className={`px-4 py-3 ${className}`}>
    <button type="button" data-testid={`payment-sort-${field}`} onClick={() => onSort(field)}
      className="inline-flex items-center gap-1 font-medium hover:text-slate-900">
      {label}
      {sort === field && (dir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
    </button>
  </th>
);

const MobileCard = ({ p, actions }) => (
  <div className="p-4 border-b" data-testid={`payment-card-row-${p.mercury_txn_id}`}>
    <div className="flex justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium text-slate-900 truncate">{p.counterparty_name || 'Unknown'}</p>
        <p className="text-xs text-slate-500">{fmtDateET(p.created_at)}</p>
      </div>
      <p className="font-semibold text-slate-900 whitespace-nowrap">{fmtMoney(p.amount)}</p>
    </div>
    <p className="text-xs text-slate-500 mt-1 break-words line-clamp-2" title={p.external_memo}>{p.external_memo}</p>
    <div className="flex flex-wrap items-center gap-2 mt-2"><StatusPill status={p.match_status} /><QboRefs payment={p} /></div>
    <div className="mt-2">{actions(p)}</div>
  </div>
);

export const PaymentsList = ({ payments, sort, dir, onSort, handlers }) => {
  const actions = (p) => <PaymentRowActions payment={p} {...handlers} />;
  if (payments.length === 0) {
    return <div className="p-8 text-center text-slate-500" data-testid="payment-list-empty">No payments for these filters.</div>;
  }
  return (
    <>
      <div className="md:hidden" data-testid="payment-list-mobile">
        {payments.map((p) => <MobileCard key={p.mercury_txn_id} p={p} actions={actions} />)}
      </div>
      <div className="hidden md:block overflow-x-auto" data-testid="payment-list-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <SortHeader label="Date" field="date" sort={sort} dir={dir} onSort={onSort} />
              <SortHeader label="Vendor" field="vendor" sort={sort} dir={dir} onSort={onSort} />
              <th className="px-4 py-3 font-medium">Memo</th>
              <SortHeader label="Amount" field="amount" sort={sort} dir={dir} onSort={onSort} className="text-right" />
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">QBO</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {payments.map((p) => (
              <tr key={p.mercury_txn_id} className="hover:bg-slate-50" data-testid={`payment-row-${p.mercury_txn_id}`}>
                <td className="px-4 py-3 whitespace-nowrap">{fmtDateET(p.created_at)}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{p.counterparty_name || 'Unknown'}</td>
                <td className="px-4 py-3 max-w-xs truncate text-slate-500" title={p.external_memo}>{p.external_memo}</td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{fmtMoney(p.amount)}</td>
                <td className="px-4 py-3" title={p.note || ''}><StatusPill status={p.match_status} /></td>
                <td className="px-4 py-3"><QboRefs payment={p} /></td>
                <td className="px-4 py-3">{actions(p)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
